import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { View, Header, CloseX, Touchable, styled } from 'modules/styled';
import { hideModal } from 'modules/navigation/navigation.actions';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

const DEFAULT_WIDTH = 95;
const DEFAULT_PADDING = 6;

const ModalParent = styled(View)({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  maxWidth: '100vw',
  height: '100vh',
  bg: 'modalBackground',
  zIndex: 100,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  willChainge: 'transform',
  overflowY: 'scroll',
  overflowX: 'hidden',
  webkitOverflowScrolling: 'touch',
});

const ModalScroll = styled(View)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  minHeight: '100vh',
  justifyContent: 'center',
});

ModalComponent.propTypes = {
  header: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  hideX: PropTypes.bool,
  children: PropTypes.node,
  footer: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
  padding: PropTypes.number,
  close: PropTypes.func,
};

function ModalComponent(props) {
  const { footer, children, hideX, maxWidth, padding, header, title } = props;

  const dispatch = useDispatch();
  const close = props.close || (() => dispatch(hideModal()));

  const modalElement = useRef(null);

  const headerEl = header || title;
  const footerEl = typeof footer === 'function' ? footer(props) : footer;

  useEffect(() => {
    const el = modalElement.current;
    disableBodyScroll(el);
    return () => enableBodyScroll(el);
  }, [modalElement]);

  const closeModal = useCallback(() => close(), [close]);

  useEffect(() => {
    const escFunction = (e) => (e.keyCode === 27 ? closeModal() : null);
    document.addEventListener('keydown', escFunction, false);
    return () => document.removeEventListener('keydown', escFunction, false);
  }, [closeModal]);

  return (
    <ModalParent onClick={closeModal} ref={modalElement}>
      <ModalScroll ignoreScrollLock>
        <View
          ignoreScrollLock
          sx={{
            px: padding || [3, DEFAULT_PADDING],
            py: padding || DEFAULT_PADDING,
            zIndex: 10,
            maxWidth: '100vw',
            width: maxWidth?.map((w) => w * 8) || ['100vw', DEFAULT_WIDTH * 8],
            bg: 'white',
            minHeight: [maxWidth?.map((w) => w * 8) ? 'auto' : '100vh', 'auto'],
            variant: 'borders.popupBorder',
            my: [0, 6],
            flexDirection: 'column',
            justifyContent: ['center', 'space-between'],
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {hideX ? null : (
            <Touchable
              onPress={closeModal}
              style={{ zIndex: 10, position: 'absolute', top: 0, right: 0 }}
            >
              <CloseX
                sx={{
                  width: 20.01,
                  height: 20.01,
                  top: [4, 6],
                  right: [3, 6],
                }}
                resizeMode={'contain'}
                source={require('public/img/x.png')}
              />
            </Touchable>
          )}
          {headerEl ? (
            <Header sx={{ pr: 5, mb: 3, flexShrink: 1 }}>{headerEl}</Header>
          ) : null}
          {children}
          {footerEl && <View sx={{ mt: 6 }}>{footerEl}</View>}
        </View>
      </ModalScroll>
    </ModalParent>
  );
}

export default (props) => {
  const modal = useSelector((state) => state.navigation.modal);
  const { name } = props;
  const visible = name && name === modal;
  return visible && document
    ? ReactDOM.createPortal(<ModalComponent {...props} />, document.body)
    : null;
};
